import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getTeams, createTeam, deleteTeam } from '../utils/api';
import AgentLayout from '../components/AgentLayout';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminTeams = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [teams, setTeams] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newTeam, setNewTeam] = useState({ name: '', description: '', parentTeam: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('agentToken');
    const info = localStorage.getItem('agentInfo');
    if (!token || !info) { navigate('/agent/login'); return; }
    const parsed = JSON.parse(info);
    if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
      navigate('/agent/dashboard'); return;
    }
    setAgentInfo(parsed);
    loadData(token);
  }, [navigate]);

  const loadData = async (token) => {
    try {
      setIsLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      const [teamsRes, agentsRes] = await Promise.all([
        getTeams(token),
        axios.get(`${API}/admin/agents`, { headers }),
      ]);
      setTeams(teamsRes.teams || []);
      setAgents(agentsRes.data.agents || []);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const token = localStorage.getItem('agentToken');

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newTeam.name.trim()) { toast.error('Team name is required'); return; }
    try {
      const result = await createTeam({
        name: newTeam.name,
        description: newTeam.description,
        parentTeam: newTeam.parentTeam || null
      }, token);
      if (result.success) {
        toast.success(`Team ${result.team.name} created`);
        setShowCreateForm(false);
        setNewTeam({ name: '', description: '', parentTeam: '' });
        loadData(token);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create team');
    }
  };

  const handleDelete = async (teamName) => {
    try {
      const result = await deleteTeam(teamName, token);
      if (result.success) {
        toast.success(result.message);
        setDeleteConfirm(null);
        loadData(token);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete team');
    }
  };

  const getTeamMemberCount = (teamName) => agents.filter(a => (a.teams || []).includes(teamName) && a.status === 'approved').length;

  if (isLoading) {
    return (
      <AgentLayout title="Teams">
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="Teams Management">
      <div data-testid="admin-teams-page">
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600 text-sm">{teams.length} team(s) configured</p>
          <button onClick={() => setShowCreateForm(!showCreateForm)}
            className="flex items-center gap-2 px-4 py-2 bg-brand-blue text-white rounded-md text-sm font-semibold hover:bg-brand-orange transition"
            data-testid="create-team-btn">
            <Plus size={16} /> Create Team
          </button>
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-testid="create-team-form">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Create New Team</h3>
            <form onSubmit={handleCreate} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Team Name *</label>
                <input type="text" value={newTeam.name} onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  placeholder="e.g., ALPHA" className="w-full px-3 py-2 border rounded-md text-sm" data-testid="team-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input type="text" value={newTeam.description} onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  placeholder="Team description" className="w-full px-3 py-2 border rounded-md text-sm" data-testid="team-desc-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent Team</label>
                <select value={newTeam.parentTeam} onChange={(e) => setNewTeam({ ...newTeam, parentTeam: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md text-sm" data-testid="team-parent-select">
                  <option value="">None (top-level)</option>
                  {teams.map(t => <option key={t.name} value={t.name}>{t.name} - {t.description}</option>)}
                </select>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-brand-blue text-white rounded-md text-sm font-semibold hover:bg-brand-orange" data-testid="team-submit-btn">Create</button>
                <button type="button" onClick={() => setShowCreateForm(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm">Cancel</button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <div key={team.name} className="bg-white rounded-lg shadow-md p-6" data-testid={`team-card-${team.name}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="text-xl font-bold text-brand-blue">{team.name}</h3>
                  <p className="text-sm text-gray-500">{team.description}</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center">
                  <Users size={20} className="text-brand-blue" />
                </div>
              </div>
              {team.parentTeam && (
                <p className="text-xs text-gray-400 mb-2">Parent: <span className="font-semibold">{team.parentTeam}</span></p>
              )}
              <div className="flex items-center justify-between mt-4">
                <span className="text-2xl font-bold text-gray-900">{getTeamMemberCount(team.name)}</span>
                <span className="text-xs text-gray-500">active members</span>
              </div>
              {agentInfo?.role === 'super_admin' && (
                <div className="mt-4 pt-3 border-t">
                  {deleteConfirm === team.name ? (
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={14} className="text-red-600" />
                      <span className="text-xs text-red-600">Delete this team?</span>
                      <button onClick={() => handleDelete(team.name)} className="px-2 py-1 bg-red-600 text-white rounded text-xs">Yes</button>
                      <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs">No</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(team.name)} className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700" data-testid={`delete-team-${team.name}`}>
                      <Trash2 size={12} /> Delete Team
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AgentLayout>
  );
};

export default AdminTeams;
